import { Action, Dispatch } from 'redux';

import { IAppState } from '../reducers';
import {
  IPublishSettings,
  createPublishSettings,
  getAllPublishSettings,
  deleteSettings,
  updateSettings,
  resendPublishConsentEmail,
  ICreateablePublishSettings,
  IUpdateablePublishSettings,
} from '../../api/vault/publishEndpoints';

export enum ActionType {
  StartSave = 'PublishEndpoints.StartSave',
  FinishSave = 'PublishEndpoints.FinishSave',
  ErrorSave = 'PublishEndpoints.ErrorSave',

  StartLoad = 'PublishEndpoints.StartLoad',
  FinishLoad = 'PublishEndpoints.FinishLoad',
  ErrorLoad = 'PublishEndpoints.ErrorLoad',

  StartUpdate = 'PublishEndpoints.StartUpdate',
  FinishUpdate = 'PublishEndpoints.FinishUpdate',
  ErrorUpdate = 'PublishEndpoints.ErrorUpdate',

  StartDelete = 'PublishEndpoints.StartDelete',
  FinishDelete = 'PublishEndpoints.FinishDelete',
  ErrorDelete = 'PublishEndpoints.ErrorDelete',

  StartResendConsent = 'PublishEndpoints.StartResendConsent',
  FinishResendConsent = 'PublishEndpoints.FinishResendConsent',
  ErrorResendConsent = 'PublishEndpoints.ErrorResendConsent'
}

export interface IPublishSettingsAction extends Action<ActionType> {
  payload: any
}

export interface IState {
  saveState: IPublishSettingsSaveState,
  loadState: IPublishSettingsLoadState,
  updateState: IPublishSettingsUpdateState,
  deleteState: IPublishSettingsDeleteState,
  consentState: IPublishSettingsConsentState,
  isWorking: boolean
}

export interface IPublishSettingsSaveState {
  isSaving: boolean,
  responseState?: any,
  error?: Error
}

export interface IPublishSettingsLoadState {
  isLoading: boolean,
  responseState?: IPublishSettings<any>[],
  error?: Error
}

export interface IPublishSettingsUpdateState {
  isUpdating: boolean,
  responseState?: any,
  error?: Error
}

export interface IPublishSettingsDeleteState {
  isDeleting: boolean,
  responseState?: any,
  error?: Error
}

export interface IPublishSettingsConsentState {
  isResending: boolean,
  responseState?: any,
  error?: Error
}

const INITIAL_STATE: IState = {
  saveState: {
    isSaving: false,
  },
  loadState: {
    isLoading: false,
  },
  deleteState: {
    isDeleting: false,
  },
  updateState: {
    isUpdating: false,
  },
  consentState: {
    isResending: false,
  },
  isWorking: false,
};

export function publishSettings(state: IState = INITIAL_STATE, action: IPublishSettingsAction) {
  switch (action.type) {
    case ActionType.StartSave:
    case ActionType.FinishSave:
    case ActionType.ErrorSave:
    case ActionType.StartLoad:
    case ActionType.FinishLoad:
    case ActionType.ErrorLoad:
    case ActionType.StartDelete:
    case ActionType.FinishDelete:
    case ActionType.ErrorDelete:
      return {
        ...state,
        ...action.payload,
      };
    default:
      return state;
  }
}

export const addPublishSettingsLocallyAction = (settings: IPublishSettings<any>[]) => async (dispatch: Dispatch<IPublishSettingsAction>, getState: () => IAppState) => {
  const state = getState();

  const clone = { ...state.publishSettings.loadState };
  clone.responseState.push(...settings);

  dispatch({
    payload: {
      loadState: clone,
    },
    type: ActionType.StartLoad,
  });
};

export const getAllSettingsAction = () => async (dispatch: Dispatch<IPublishSettingsAction>, getState: () => IAppState) => {
  const state = getState();
  dispatch({
    payload: {
      loadState: {
        isLoading: true,
        responseState: state.publishSettings.loadState.responseState,
      },
      isWorking: true,
    },
    type: ActionType.StartLoad,
  });

  try {
    const response = await getAllPublishSettings(state.user.impersonation.user);

    if (response.status === 200) {
      dispatch({
        payload: {
          loadState: {
            isLoading: false,
            responseState: response.data.data,
          },
          isWorking: false,
        },
        type: ActionType.FinishLoad,
      });
    }
  } catch (e) {
    dispatch({
      payload: {
        loadState: {
          isLoading: false,
          responseState: null,
          error: e,
        },
        isWorking: false,
      },
      type: ActionType.ErrorLoad,
    });
  }
};

export const createSettingsAction = (params: ICreateablePublishSettings<any>) => async (dispatch: Dispatch<IPublishSettingsAction>, getState: () => IAppState) => {
  dispatch({
    payload: {
      saveState: {
        isSaving: true,
      },
      isWorking: true,
    },
    type: ActionType.StartSave,
  });

  try {
    const response = await createPublishSettings(params);

    if (response.status === 200) {
      dispatch({
        payload: {
          saveState: {
            isSaving: false,
            responseState: response,
          },
          isWorking: false,
        },
        type: ActionType.FinishSave,
      });
    }
  } catch (e) {
    dispatch({
      payload: {
        saveState: {
          isSaving: false,
          responseState: null,
          error: e,
        },
        isWorking: false,
      },
      type: ActionType.ErrorSave,
    });
  }
};

export const updateSettingsAction = (id: string, params: IUpdateablePublishSettings<any>) => async (dispatch: Dispatch<IPublishSettingsAction>, getState: () => IAppState) => {
  dispatch({
    payload: {
      updateState: {
        isUpdating: true,
      },
      isWorking: true,
    },
    type: ActionType.StartUpdate,
  });

  try {
    const response = await updateSettings(id, params);

    if (response.status === 200) {
      dispatch({
        payload: {
          updateState: {
            isUpdating: false,
            responseState: response,
          },
          isWorking: false,
        },
        type: ActionType.FinishUpdate,
      });
    }
  } catch (e) {
    dispatch({
      payload: {
        updateState: {
          isUpdating: false,
          responseState: null,
          error: e,
        },
        isWorking: false,
      },
      type: ActionType.ErrorUpdate,
    });
  }
};

export const deleteSettingsAction = (id: string) => async (dispatch: Dispatch<IPublishSettingsAction>, getState: () => IAppState) => {
  dispatch({
    payload: {
      deleteState: {
        isDeleting: true,
      },
      isWorking: true,
    },
    type: ActionType.StartDelete,
  });

  try {
    const response = await deleteSettings(id);

    if (response.status === 200) {
      dispatch({
        payload: {
          deleteState: {
            isDeleting: false,
            responseState: response,
          },
          isWorking: false,
        },
        type: ActionType.FinishDelete,
      });
    }
  } catch (e) {
    dispatch({
      payload: {
        deleteState: {
          isDeleting: false,
          responseState: null,
          error: e,
        },
        isWorking: false,
      },
      type: ActionType.ErrorDelete,
    });
  }
};

export const resendPublishConsentEmailAction = (id: string) => async (dispatch: Dispatch<IPublishSettingsAction>, getState: () => IAppState) => {
  dispatch({
    payload: {
      consentState: {
        isResending: true,
      },
      isWorking: true,
    },
    type: ActionType.StartResendConsent,
  });

  try {
    const response = await resendPublishConsentEmail(id);

    if (response.status === 200) {
      dispatch({
        payload: {
          consentState: {
            isResending: false,
            responseState: response,
          },
          isWorking: false,
        },
        type: ActionType.FinishResendConsent,
      });
    }
  } catch (e) {
    dispatch({
      payload: {
        consentState: {
          isResending: false,
          responseState: null,
          error: e,
        },
        isWorking: false,
      },
      type: ActionType.ErrorResendConsent,
    });
  }
};
